import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  X,
  Columns,
  SlidersHorizontal,
  GripVertical,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tags,
  Check,
  ShieldAlert,
  Globe,
  Eye,
  EyeOff,
  Clock,
  CalendarClock,
  Rows3,
  Rows2,
  SearchX,
} from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import RegistrarLabel from '../components/CurrentRegistrar';
import { useToast } from '../components/ui/Toast';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';
import DomainViews from '../components/DomainViews';
import { Domain, RegistrarAccountOption, Tag, ApiResponse, NsChange } from '../types';
import { LivenessDot, LivenessBadge } from '../components/LivenessBadge';
import SearchableSelect, { type SearchableOption } from '../components/SearchableSelect';
import NameserverEditModal from '../components/NameserverEditModal';
import RenewModal from '../components/RenewModal';
import { registrarName } from '../lib/registrar-names';
import { logoUrlFor } from '../lib/registrar-logos';
import { format } from 'date-fns';
import AddDomainModal from '../components/AddDomainModal';
import { GeoList } from '../components/GeoFlags';
import { parseNameserverList } from '../lib/nameservers';
import { extractDomain } from '../lib/domainInput';

/** Search box text: the bare domain once the input is a URL that contains one. */
const toSearchText = (v: string) => { const d = extractDomain(v); return d !== v && d.includes('.') ? d : v; };
import DomainStatusBadge from '../components/DomainStatusBadge';
import TierBadge from '../components/TierBadge';
import { ANY_GEO, flagUrl, geoName, isIsoGeo } from '../lib/geo';

type ColumnKey =
  | 'domain'
  | 'tld'
  | 'registrar'
  | 'expiry'
  | 'status'
  | 'liveness'
  | 'tier'
  | 'autoRenew'
  | 'tags'
  | 'vertical'
  | 'niche'
  | 'subniche'
  | 'geo'
  | 'cloakingType'
  | 'subreddit'
  | 'domainType'
  | 'source'
  | 'nameservers'
  | 'acquisitionType'
  | 'cost'
  | 'acquired';

interface ColumnDef {
  key: ColumnKey;
  label: string;
  defaultVisible: boolean;
  /** API query param key for filtering, or null if not server-filterable */
  filterParam: string | null;
  /** 'text'=free text, 'select'=dropdown, 'numberRange'/'dateRange'=two-input
   *  range (uses rangeParams), null=no filter */
  filterType: 'text' | 'select' | 'numberRange' | 'dateRange' | null;
  /** For range filters: the [min, max] / [after, before] query-param pair. */
  rangeParams?: [string, string];
  /** sort_by value sent to API */
  sortKey: string | null;
  /** Visibility flag this column requires: only offered to privy callers. */
  requires?: 'cost' | 'acquisition';
}

// Classification columns get a distinct background tint
const classificationColumns = new Set<ColumnKey>(['tier', 'vertical', 'niche', 'subniche', 'geo', 'cloakingType', 'subreddit']);

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'domain', label: 'Domain', defaultVisible: true, filterParam: null, filterType: null, sortKey: 'domain_name' },
  { key: 'domainType', label: 'Type', defaultVisible: true, filterParam: 'domain_type', filterType: 'select', sortKey: 'domain_type' },
  { key: 'tier', label: 'Tier', defaultVisible: true, filterParam: 'tier', filterType: 'select', sortKey: 'tier' },
  { key: 'tld', label: 'TLD', defaultVisible: true, filterParam: 'tld', filterType: 'select', sortKey: 'tld' },
  { key: 'registrar', label: 'Registrar', defaultVisible: true, filterParam: 'registrar_id', filterType: 'select', sortKey: null },
  { key: 'expiry', label: 'Expiry Date', defaultVisible: true, filterParam: null, filterType: null, sortKey: 'expiry_date' },
  { key: 'status', label: 'Status', defaultVisible: true, filterParam: 'status', filterType: 'select', sortKey: 'status' },
  { key: 'liveness', label: 'Liveness', defaultVisible: true, filterParam: 'liveness_status', filterType: 'select', sortKey: 'liveness_status' },
  { key: 'autoRenew', label: 'Auto-Renew', defaultVisible: true, filterParam: 'auto_renew', filterType: 'select', sortKey: 'auto_renew' },
  { key: 'tags', label: 'Tags', defaultVisible: true, filterParam: 'tag_id', filterType: 'select', sortKey: null },
  { key: 'vertical', label: 'Vertical', defaultVisible: true, filterParam: 'vertical', filterType: 'select', sortKey: 'vertical' },
  { key: 'niche', label: 'Niche', defaultVisible: true, filterParam: 'niche', filterType: 'select', sortKey: 'niche' },
  { key: 'subniche', label: 'Sub-Niche', defaultVisible: true, filterParam: 'subniche', filterType: 'select', sortKey: 'subniche' },
  { key: 'geo', label: 'Geo', defaultVisible: true, filterParam: 'geo', filterType: 'select', sortKey: 'geo' },
  { key: 'cloakingType', label: 'Cloaking', defaultVisible: true, filterParam: 'cloaking_type', filterType: 'select', sortKey: 'cloaking_type' },
  { key: 'subreddit', label: 'Subreddit', defaultVisible: true, filterParam: 'subreddit', filterType: 'select', sortKey: 'subreddit' },
  { key: 'source', label: 'Source', defaultVisible: true, filterParam: 'source', filterType: 'select', sortKey: 'source' },
  { key: 'nameservers', label: 'Nameservers', defaultVisible: true, filterParam: null, filterType: null, sortKey: null },
  // Optional, permission-gated columns. Hidden by default.
  { key: 'acquisitionType', label: 'Acquisition Type', defaultVisible: false, filterParam: 'acquisition_method', filterType: 'select', sortKey: 'acquisition_method', requires: 'acquisition' },
  { key: 'cost', label: 'Cost', defaultVisible: false, filterParam: null, filterType: 'numberRange', rangeParams: ['cost_min', 'cost_max'], sortKey: 'cost', requires: 'cost' },
  { key: 'acquired', label: 'Date Acquired', defaultVisible: false, filterParam: null, filterType: 'dateRange', rangeParams: ['acquired_after', 'acquired_before'], sortKey: 'acquisition_date', requires: 'acquisition' },
];

const COL_MAP = new Map(ALL_COLUMNS.map(c => [c.key, c]));

function formatCost(cost: number | null, currency: string | null): string {
  if (cost === null || cost === undefined) return '—';
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cost);
    } catch { /* invalid code, fall through */ }
    return `${cost.toFixed(2)} ${currency}`;
  }
  return cost.toFixed(2);
}

const VISIBILITY_KEY = 'domains-visible-columns';
const ORDER_KEY = 'domains-column-order';
const SHOW_COL_FILTERS_KEY = 'domains-show-col-filters';
const DENSITY_KEY = 'domains-density';

// Columns the browser already knew about when visibility was last saved, so a
// column added later (e.g. Tier) shows by default without un-hiding ones the
// user turned off.
const KNOWN_COLUMNS_KEY = 'domains-known-columns';

function getInitialVisibility(): Set<ColumnKey> {
  try {
    const stored = localStorage.getItem(VISIBILITY_KEY);
    if (stored) {
      const visible = new Set(JSON.parse(stored) as ColumnKey[]);
      const known = new Set(JSON.parse(localStorage.getItem(KNOWN_COLUMNS_KEY) || 'null') ?? ALL_COLUMNS.map(c => c.key).filter(k => k !== 'tier'));
      for (const c of ALL_COLUMNS) if (c.defaultVisible && !known.has(c.key)) visible.add(c.key);
      // Save both together so the new column stays visible on later loads.
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify([...visible]));
      localStorage.setItem(KNOWN_COLUMNS_KEY, JSON.stringify(ALL_COLUMNS.map(c => c.key)));
      return visible;
    }
  } catch { /* ignore */ }
  return new Set(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
}

// Bump this version when column order changes to reset saved order
const ORDER_VERSION = 3;
const ORDER_VERSION_KEY = 'domains-column-order-v';

function getInitialOrder(): ColumnKey[] {
  try {
    const savedVersion = localStorage.getItem(ORDER_VERSION_KEY);
    if (savedVersion && parseInt(savedVersion) >= ORDER_VERSION) {
      const stored = localStorage.getItem(ORDER_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ColumnKey[];
        const known = new Set(parsed);
        const extra = ALL_COLUMNS.filter(c => !known.has(c.key)).map(c => c.key);
        return [...parsed, ...extra];
      }
    }
    // Version mismatch or no saved order — reset to default
    localStorage.removeItem(ORDER_KEY);
    localStorage.setItem(ORDER_VERSION_KEY, ORDER_VERSION.toString());
  } catch { /* ignore */ }
  return ALL_COLUMNS.map(c => c.key);
}

export default function DomainsPage() {
  const { fetchApi, token, isAdmin, canClassify, canManageNameservers, canRenewDomains, canSeeWhois, canSeeCost, canSeeAcquisition } = useApi();
  const toast = useToast();
  // Row selection drives the classify bulk actions, bulk NS edits and bulk renewals.
  const canSelect = canClassify || canManageNameservers || canRenewDomains;
  const [renewOpen, setRenewOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [accounts, setAccounts] = useState<RegistrarAccountOption[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [tlds, setTlds] = useState<string[]>([]);
  const [metaOptions, setMetaOptions] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);

  // Bulk selection + actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Costs are blurred by default so a whole column of them isn't readable over
  // a shoulder or in a screen share. Two ways to reveal: the eye in the Cost
  // header unblurs the column, or clicking one cell unblurs just that row.
  // Neither persists — reloading or leaving the page re-blurs everything.
  const [costsRevealed, setCostsRevealed] = useState(false);
  const [revealedCostIds, setRevealedCostIds] = useState<Set<string>>(new Set());

  const toggleCostColumn = () => {
    // Hiding the column should hide everything, including per-row reveals.
    if (costsRevealed) setRevealedCostIds(new Set());
    setCostsRevealed((v) => !v);
  };

  const toggleCostRow = (id: string) => {
    setRevealedCostIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const [bulkBusy, setBulkBusy] = useState(false);
  const [nsBulkOpen, setNsBulkOpen] = useState(false);
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);
  const [tagMode, setTagMode] = useState<'add' | 'remove' | 'set'>('add');
  const [tagDraft, setTagDraft] = useState<Set<string>>(new Set());
  const tagPopoverRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(getInitialVisibility);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef<HTMLDivElement>(null);

  // Column ordering
  const [columnOrder, setColumnOrder] = useState<ColumnKey[]>(getInitialOrder);

  // Per-column filter row
  const [showColFilters, setShowColFilters] = useState(() => {
    try { return localStorage.getItem(SHOW_COL_FILTERS_KEY) === 'true'; } catch { return false; }
  });
  const [colFilterDrafts, setColFilterDrafts] = useState<Record<string, string>>({});
  // Row density (per browser).
  const [compact, setCompact] = useState(() => {
    try { return localStorage.getItem(DENSITY_KEY) === 'compact'; } catch { return false; }
  });
  const toggleDensity = () => setCompact((c) => {
    try { localStorage.setItem(DENSITY_KEY, c ? 'comfortable' : 'compact'); } catch { /* ignore */ }
    return !c;
  });
  const filterTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Drag state
  const dragCol = useRef<ColumnKey | null>(null);
  const dragOverCol = useRef<ColumnKey | null>(null);

  // Read filter values from URL
  const page = parseInt(searchParams.get('page') || '1');
  const limit = 25;
  const search = searchParams.get('search') || '';
  const registrarFilter = searchParams.get('registrar_id') || '';
  const tagFilter = searchParams.get('tag_id') || '';
  const statusFilter = searchParams.get('status') || '';
  const autoRenewFilter = searchParams.get('auto_renew') || '';
  const sourceFilter = searchParams.get('source') || '';
  const tldFilter = searchParams.get('tld') || '';
  const expiringDaysFilter = searchParams.get('expiring_days') || '';
  const sortBy = searchParams.get('sort_by') || '';
  const sortOrder = (searchParams.get('sort_order') || '') as '' | 'asc' | 'desc';

  // Metadata column filters from URL
  const verticalFilter = searchParams.get('vertical') || '';
  const nicheFilter = searchParams.get('niche') || '';
  const subnicheFilter = searchParams.get('subniche') || '';
  const geoFilter = searchParams.get('geo') || '';
  const livenessFilter = searchParams.get('liveness_status') || '';
  const exposedFilter = searchParams.get('exposed') || '';
  const movedAwayFilter = searchParams.get('moved_away') || '';
  const renewalMissedFilter = searchParams.get('renewal_missed') || '';
  // Expiry month range (from the dashboard's renewals chart): from inclusive, to exclusive.
  const expiresFromFilter = searchParams.get('expires_from') || '';
  const expiresToFilter = searchParams.get('expires_to') || '';
  const geoMismatchFilter = searchParams.get('geo_mismatch') || '';
  const tierFilter = searchParams.get('tier') || '';
  const cloakingTypeFilter = searchParams.get('cloaking_type') || '';
  const domainTypeFilter = searchParams.get('domain_type') || '';
  const subredditFilter = searchParams.get('subreddit') || '';
  const acquisitionMethodFilter = searchParams.get('acquisition_method') || '';
  const acquiredAfterFilter = searchParams.get('acquired_after') || '';
  const acquiredBeforeFilter = searchParams.get('acquired_before') || '';
  const costMinFilter = searchParams.get('cost_min') || '';
  const costMaxFilter = searchParams.get('cost_max') || '';

  // Initialize column filter drafts from URL once
  useEffect(() => {
    const initial: Record<string, string> = {};
    if (search) initial['search'] = search;
    if (statusFilter) initial['status'] = statusFilter;
    if (registrarFilter) initial['registrar_id'] = registrarFilter;
    if (tagFilter) initial['tag_id'] = tagFilter;
    if (autoRenewFilter) initial['auto_renew'] = autoRenewFilter;
    if (sourceFilter) initial['source'] = sourceFilter;
    if (tldFilter) initial['tld'] = tldFilter;
    if (verticalFilter) initial['vertical'] = verticalFilter;
    if (nicheFilter) initial['niche'] = nicheFilter;
    if (subnicheFilter) initial['subniche'] = subnicheFilter;
    if (geoFilter) initial['geo'] = geoFilter;
    if (livenessFilter) initial['liveness_status'] = livenessFilter;
    if (tierFilter) initial['tier'] = tierFilter;
    if (domainTypeFilter) initial['domain_type'] = domainTypeFilter;
    if (cloakingTypeFilter) initial['cloaking_type'] = cloakingTypeFilter;
    if (subredditFilter) initial['subreddit'] = subredditFilter;
    if (acquisitionMethodFilter) initial['acquisition_method'] = acquisitionMethodFilter;
    if (acquiredAfterFilter) initial['acquired_after'] = acquiredAfterFilter;
    if (acquiredBeforeFilter) initial['acquired_before'] = acquiredBeforeFilter;
    if (costMinFilter) initial['cost_min'] = costMinFilter;
    if (costMaxFilter) initial['cost_max'] = costMaxFilter;
    setColFilterDrafts(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const anyFilterActive = !!(search || registrarFilter || tagFilter || statusFilter || autoRenewFilter || sourceFilter || tldFilter || expiringDaysFilter || domainTypeFilter || verticalFilter || nicheFilter || subnicheFilter || geoFilter || livenessFilter || exposedFilter || movedAwayFilter || renewalMissedFilter || expiresFromFilter || expiresToFilter || geoMismatchFilter || tierFilter || cloakingTypeFilter || subredditFilter || acquisitionMethodFilter || acquiredAfterFilter || acquiredBeforeFilter || costMinFilter || costMaxFilter);

  // ── Helpers ──

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      localStorage.setItem(KNOWN_COLUMNS_KEY, JSON.stringify(ALL_COLUMNS.map(c => c.key)));
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const updateFilter = useCallback((key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.set('page', '1');
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const setColFilter = useCallback((paramKey: string, value: string) => {
    setColFilterDrafts(prev => ({ ...prev, [paramKey]: value }));
    // Debounce text filters; apply selects immediately
    if (filterTimers.current[paramKey]) clearTimeout(filterTimers.current[paramKey]);
    filterTimers.current[paramKey] = setTimeout(() => {
      updateFilter(paramKey, paramKey === 'search' ? toSearchText(value) : value);
    }, 350);
  }, [updateFilter]);

  const setColFilterImmediate = useCallback((paramKey: string, value: string) => {
    setColFilterDrafts(prev => ({ ...prev, [paramKey]: value }));
    updateFilter(paramKey, value);
  }, [updateFilter]);

  const toggleSort = useCallback((sortKey: string) => {
    const params = new URLSearchParams(searchParams);
    if (sortBy === sortKey && sortOrder === 'asc') {
      params.set('sort_by', sortKey);
      params.set('sort_order', 'desc');
    } else if (sortBy === sortKey && sortOrder === 'desc') {
      params.delete('sort_by');
      params.delete('sort_order');
    } else {
      params.set('sort_by', sortKey);
      params.set('sort_order', 'asc');
    }
    params.set('page', '1');
    setSearchParams(params);
  }, [searchParams, setSearchParams, sortBy, sortOrder]);

  // Close column picker on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target as Node))
        setShowColumnPicker(false);
    }
    if (showColumnPicker) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showColumnPicker]);


  // ── Data fetching ──

  /** The current filters and sort as query params — shared by the list and the CSV export. */
  const filterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (registrarFilter) params.set('registrar_id', registrarFilter);
    if (tagFilter) params.set('tag_id', tagFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (autoRenewFilter) params.set('auto_renew', autoRenewFilter);
    if (sourceFilter) params.set('source', sourceFilter);
    if (tldFilter) params.set('tld', tldFilter);
    if (expiringDaysFilter) params.set('expiring_days', expiringDaysFilter);
    if (sortBy) params.set('sort_by', sortBy);
    if (sortOrder) params.set('sort_order', sortOrder);
    if (verticalFilter) params.set('vertical', verticalFilter);
    if (nicheFilter) params.set('niche', nicheFilter);
    if (subnicheFilter) params.set('subniche', subnicheFilter);
    if (geoFilter) params.set('geo', geoFilter);
    if (livenessFilter) params.set('liveness_status', livenessFilter);
    if (exposedFilter) params.set('exposed', exposedFilter);
    if (movedAwayFilter) params.set('moved_away', movedAwayFilter);
    if (renewalMissedFilter) params.set('renewal_missed', renewalMissedFilter);
    if (expiresFromFilter) params.set('expires_from', expiresFromFilter);
    if (expiresToFilter) params.set('expires_to', expiresToFilter);
    if (geoMismatchFilter) params.set('geo_mismatch', geoMismatchFilter);
    if (tierFilter) params.set('tier', tierFilter);
    if (domainTypeFilter) params.set('domain_type', domainTypeFilter);
    if (cloakingTypeFilter) params.set('cloaking_type', cloakingTypeFilter);
    if (subredditFilter) params.set('subreddit', subredditFilter);
    if (acquisitionMethodFilter) params.set('acquisition_method', acquisitionMethodFilter);
    if (acquiredAfterFilter) params.set('acquired_after', acquiredAfterFilter);
    if (acquiredBeforeFilter) params.set('acquired_before', acquiredBeforeFilter);
    if (costMinFilter) params.set('cost_min', costMinFilter);
    if (costMaxFilter) params.set('cost_max', costMaxFilter);
    return params;
  }, [search, registrarFilter, tagFilter, statusFilter, autoRenewFilter, sourceFilter, tldFilter, expiringDaysFilter, sortBy, sortOrder, domainTypeFilter, verticalFilter, nicheFilter, subnicheFilter, geoFilter, livenessFilter, exposedFilter, movedAwayFilter, renewalMissedFilter, expiresFromFilter, expiresToFilter, geoMismatchFilter, tierFilter, cloakingTypeFilter, subredditFilter, acquisitionMethodFilter, acquiredAfterFilter, acquiredBeforeFilter, costMinFilter, costMaxFilter]);

  const loadDomains = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set()); // selection is per-view; reset on any (re)load
    try {
      const params = filterParams();
      params.set('page', page.toString());
      params.set('limit', limit.toString());
      const res = await fetchApi<ApiResponse<Domain[]>>(`/domains?${params}`);
      if (res.data) setDomains(res.data);
      if (res.meta?.total !== undefined) setTotal(res.meta.total);
    } catch (error) {
      console.error('Failed to load domains:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchApi, page, filterParams]);

  useEffect(() => { loadDomains(); }, [loadDomains]);

  // Nameserver changes still awaiting confirmation at the registrar, keyed by
  // domain id — marked in the NS column. Refreshed with the list, and every
  // 30s while any are open (the server-side sweep does the registrar calls).
  const [openNsChanges, setOpenNsChanges] = useState<Map<string, NsChange>>(new Map());
  const loadOpenNsChanges = useCallback(async () => {
    if (!canManageNameservers) return;
    try {
      const res = await fetchApi<ApiResponse<NsChange[]>>('/ns-changes?status=pending,stalled&limit=500');
      setOpenNsChanges(new Map((res.data ?? []).map((c) => [c.domain_id, c])));
    } catch { /* non-fatal */ }
  }, [fetchApi, canManageNameservers]);
  useEffect(() => { loadOpenNsChanges(); }, [loadOpenNsChanges, domains]);
  useEffect(() => {
    if (openNsChanges.size === 0) return;
    const t = setInterval(loadOpenNsChanges, 30_000);
    return () => clearInterval(t);
  }, [openNsChanges.size, loadOpenNsChanges]);

  useEffect(() => {
    async function loadFilters() {
      try {
        const [accountsRes, tagRes] = await Promise.all([
          fetchApi<ApiResponse<RegistrarAccountOption[]>>('/accounts/options'),
          fetchApi<ApiResponse<Tag[]>>('/tags'),
        ]);
        if (accountsRes.data) setAccounts(accountsRes.data);
        if (tagRes.data) setTags(tagRes.data);
        try {
          const [tldRes, metaRes] = await Promise.all([
            fetchApi<ApiResponse<{ tld: string; count: number }[]>>('/reports/by-tld'),
            fetchApi<ApiResponse<Record<string, string[]>>>('/reports/filter-options'),
          ]);
          if (tldRes.data) setTlds(tldRes.data.map(t => t.tld));
          if (metaRes.data) setMetaOptions(metaRes.data);
        } catch { /* not critical */ }
      } catch (error) {
        console.error('Failed to load filters:', error);
      }
    }
    loadFilters();
  }, [fetchApi]);

  const totalPages = Math.ceil(total / limit);
  // Export exactly what the current filters show (all pages, same order) as CSV.
  // Admins only. The session token goes in the Authorization header, never the URL.
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`/api/domains/export?${filterParams()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? `Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'domains.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed', e instanceof Error ? e.message : undefined);
    } finally {
      setExporting(false);
    }
  };

  // ── Bulk selection ──

  const allOnPageSelected = domains.length > 0 && domains.every(d => selectedIds.has(d.id));

  const toggleRow = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) domains.forEach(d => next.delete(d.id));
      else domains.forEach(d => next.add(d.id));
      return next;
    });
  };

  // Esc clears the selection (unless a dialog or popover is handling it).
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('[role="dialog"]') && !tagPopoverOpen) setSelectedIds(new Set());
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedIds.size, tagPopoverOpen]);

  // Header checkbox shows an indeterminate state when only some rows are selected.
  useEffect(() => {
    if (selectAllRef.current) {
      const someSelected = domains.some(d => selectedIds.has(d.id));
      selectAllRef.current.indeterminate = someSelected && !allOnPageSelected;
    }
  }, [domains, selectedIds, allOnPageSelected]);

  // Close the tags popover on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (tagPopoverRef.current && !tagPopoverRef.current.contains(e.target as Node))
        setTagPopoverOpen(false);
    }
    if (tagPopoverOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [tagPopoverOpen]);

  const applyBulkType = async (domainType: string | null) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await fetchApi<ApiResponse<{ updated: number; skipped: string[] }>>('/domains/bulk-update-type', {
        method: 'POST',
        body: JSON.stringify({ domain_ids: ids, domain_type: domainType }),
      });
      const skipped = res.data?.skipped?.length || 0;
      const updated = res.data?.updated ?? 0;
      const what = domainType ? `Type set to “${domainType}”` : 'Type cleared';
      // Always confirm: the list reloads and the Type column may not be on screen.
      if (skipped > 0) toast.info(`${what} on ${updated} domain${updated === 1 ? '' : 's'}`, `${skipped} skipped (out of scope or not found).`);
      else toast.success(`${what} on ${updated} domain${updated === 1 ? '' : 's'}`);
      await loadDomains();
    } catch (error) {
      toast.error('Bulk update failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setBulkBusy(false);
    }
  };

  const applyBulkTags = async () => {
    const ids = Array.from(selectedIds);
    const tagIds = Array.from(tagDraft);
    if (ids.length === 0) return;
    if (tagMode !== 'set' && tagIds.length === 0) {
      toast.info('Select at least one tag to add or remove.');
      return;
    }
    setBulkBusy(true);
    try {
      const res = await fetchApi<ApiResponse<{ updated: number; skipped: string[] }>>('/domains/bulk-tags', {
        method: 'POST',
        body: JSON.stringify({ domain_ids: ids, tag_ids: tagIds, mode: tagMode }),
      });
      const skipped = res.data?.skipped?.length || 0;
      const updated = res.data?.updated ?? 0;
      const what = `Tags ${tagMode === 'add' ? 'added to' : tagMode === 'remove' ? 'removed from' : 'replaced on'} ${updated} domain${updated === 1 ? '' : 's'}`;
      if (skipped > 0) toast.info(what, `${skipped} skipped (out of scope or not found).`);
      else toast.success(what);
      setTagPopoverOpen(false);
      setTagDraft(new Set());
      await loadDomains();
    } catch (error) {
      toast.error('Bulk tag update failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setBulkBusy(false);
    }
  };

  const applyBulkNameservers = async (nameservers: string[]) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await fetchApi<ApiResponse<{ updated: string[]; skipped: { id: string; reason: string }[]; failed: { id: string; error: string }[]; changes?: { id: string; change_id: string }[] }>>('/domains/bulk-nameservers', {
        method: 'POST',
        body: JSON.stringify({ domain_ids: ids, nameservers }),
      });
      const d = res.data;
      setNsBulkOpen(false);
      {
        const summary = `Accepted by the registrar: ${d?.updated.length ?? 0} · skipped (not an Ilkari/Porkbun/Safenames/GoDaddy API domain): ${d?.skipped.length ?? 0} · failed: ${d?.failed.length ?? 0}\n\nAccepted domains show "pending" in the Nameservers column until the registrar confirms (Porkbun usually within minutes; Ilkari applies in the background).`;
        if ((d?.failed.length ?? 0) > 0) toast.error('Some nameserver updates failed', summary);
        else toast.success('Nameserver update submitted', summary);
      }
      await loadDomains();
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleTagDraft = (id: string) => {
    setTagDraft(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Column drag handlers ──

  const handleDragStart = (key: ColumnKey) => {
    dragCol.current = key;
  };
  const handleDragOver = (e: React.DragEvent, key: ColumnKey) => {
    e.preventDefault();
    dragOverCol.current = key;
  };
  const handleDrop = () => {
    if (!dragCol.current || !dragOverCol.current || dragCol.current === dragOverCol.current) return;
    setColumnOrder(prev => {
      const next = [...prev];
      const fromIdx = next.indexOf(dragCol.current!);
      const toIdx = next.indexOf(dragOverCol.current!);
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, dragCol.current!);
      localStorage.setItem(ORDER_KEY, JSON.stringify(next));
      localStorage.setItem(ORDER_VERSION_KEY, ORDER_VERSION.toString());
      return next;
    });
    dragCol.current = null;
    dragOverCol.current = null;
  };

  // A column is available only if the caller is privy to the field it shows.
  const canShowColumn = useCallback((col: ColumnDef) => {
    if (col.requires === 'cost') return canSeeCost;
    if (col.requires === 'acquisition') return canSeeAcquisition;
    return true;
  }, [canSeeCost, canSeeAcquisition]);
  const availableColumns = ALL_COLUMNS.filter(canShowColumn);

  // Ordered + visible columns (drop any the caller isn't allowed to see)
  const activeColumns = columnOrder
    .filter(k => visibleColumns.has(k))
    .map(k => COL_MAP.get(k)!)
    .filter(col => col && canShowColumn(col));

  // ── Render helpers ──

  function renderFilterInput(col: ColumnDef) {
    if (!col.filterType) return <div />;

    // Two-input range filters (cost / date acquired) — each end maps to its own
    // query param so it composes with the rest of the column filters.
    if ((col.filterType === 'numberRange' || col.filterType === 'dateRange') && col.rangeParams) {
      const [minKey, maxKey] = col.rangeParams;
      const isDate = col.filterType === 'dateRange';
      const minVal = colFilterDrafts[minKey] || '';
      const maxVal = colFilterDrafts[maxKey] || '';
      const cls = (v: string) =>
        `w-full text-xs border rounded px-1 py-1 bg-surface transition-colors ${v ? 'border-brand-400 bg-brand-50/40' : 'border-slate-200'} focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none placeholder:text-slate-300`;
      return (
        <div className="flex items-center gap-1">
          <input
            type={isDate ? 'date' : 'number'}
            min={isDate ? undefined : '0'}
            step={isDate ? undefined : '0.01'}
            value={minVal}
            onChange={e => setColFilter(minKey, e.target.value)}
            placeholder={isDate ? '' : 'Min'}
            aria-label={isDate ? 'Acquired after' : 'Minimum cost'}
            className={cls(minVal)}
          />
          <span className="text-slate-300 text-xs">–</span>
          <input
            type={isDate ? 'date' : 'number'}
            min={isDate ? undefined : '0'}
            step={isDate ? undefined : '0.01'}
            value={maxVal}
            onChange={e => setColFilter(maxKey, e.target.value)}
            placeholder={isDate ? '' : 'Max'}
            aria-label={isDate ? 'Acquired before' : 'Maximum cost'}
            className={cls(maxVal)}
          />
        </div>
      );
    }

    if (!col.filterParam) return <div />;
    const paramKey = col.filterParam;
    const value = colFilterDrafts[paramKey] || '';

    if (col.filterType === 'select') {
      return (
        <SearchableSelect
          value={value}
          onChange={(v) => setColFilterImmediate(paramKey, v)}
          options={getSelectOptions(col.key)}
        />
      );
    }

    return (
      <input
        type="text"
        value={value}
        onChange={e => setColFilter(paramKey, e.target.value)}
        placeholder="Filter..."
        className={`w-full text-xs border rounded px-1.5 py-1 bg-surface transition-colors ${value ? 'border-brand-400 bg-brand-50/40' : 'border-slate-200'} focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none placeholder:text-slate-300`}
      />
    );
  }

  function getSelectOptions(key: ColumnKey): SearchableOption[] {
    switch (key) {
      case 'status': return [
        { value: 'active', label: 'Active' },
        { value: 'expired', label: 'Expired' },
        { value: 'locked', label: 'Locked' },
        { value: 'pending_transfer', label: 'Pending Transfer' },
        { value: 'transferred_out', label: 'Transferred out' },
        { value: 'transfer_failed', label: 'Transfer failed' },
      ];
      case 'autoRenew': return [
        { value: 'true', label: 'On' },
        { value: 'false', label: 'Off' },
        { value: 'not_on', label: 'Off or unknown' },
      ];
      case 'liveness': return [
        { value: 'live', label: 'Live' },
        { value: 'parked', label: 'Parked' },
        { value: 'for_sale', label: 'For sale' },
        { value: 'redirect', label: 'Redirect' },
        { value: 'offline', label: 'Offline' },
        { value: 'error', label: 'Check error' },
        { value: '__none__', label: '(Never checked)' },
      ];
      case 'domainType': return [
        { value: '__none__', label: '(No type set)' },
        ...(metaOptions.domain_type || []).map(v => ({ value: v, label: v })),
      ];
      case 'acquisitionType': return [
        { value: '__none__', label: '(Not set)' },
        ...(metaOptions.acquisition_method || []).map(v => ({ value: v, label: v })),
      ];
      case 'source': return [
        { value: 'manual', label: 'Manual' },
        { value: 'csv_import', label: 'CSV Import' },
        { value: 'registrar-api', label: 'Registrar API' },
      ];
      case 'tld': return tlds.map(t => ({ value: t, label: `.${t}` }));
      case 'registrar': return [
        { value: '__no_owner__', label: '(Accounts without an owner)' },
        ...accounts.map(a => ({
          value: a.id,
          label: `${a.registrar_name} · ${a.display_name ?? a.label}`,
          logo: logoUrlFor(a.registrar_type),
        })),
      ];
      case 'tags': return [
        { value: '__none__', label: '(No tags)' },
        ...tags.map(t => ({ value: t.id, label: t.name })),
      ];
      case 'tier': return [
        { value: '__any__', label: '(Any tier)' },
        { value: '__none__', label: '(No tier)' },
        ...(metaOptions.tier || []).map(v => ({ value: v, label: v })),
      ];
      case 'vertical': return [
        { value: '__none__', label: '(No vertical set)' },
        ...(metaOptions.vertical || []).map(v => ({ value: v, label: v })),
      ];
      case 'niche': return (metaOptions.niche || []).map(v => ({ value: v, label: v }));
      case 'subniche': return (metaOptions.subniche || []).map(v => ({ value: v, label: v }));
      case 'geo': return [
        { value: '__none__', label: '(No geo set)' },
        ...(metaOptions.geo || []).map(v => ({
          value: v,
          label: v === ANY_GEO ? 'Any geo' : isIsoGeo(v) ? `${geoName(v)} (${v})` : v,
          logo: flagUrl(v),
        })),
      ];
      case 'cloakingType': return (metaOptions.cloaking_type || []).map(v => ({ value: v, label: v }));
      case 'subreddit': return (metaOptions.subreddit || []).map(v => ({ value: v, label: v }));
      default: return [];
    }
  }

  function renderCellValue(col: ColumnDef, domain: Domain & { registrar_name?: string; tags?: Tag[] }) {
    switch (col.key) {
      case 'domain':
        return (
          <span className="inline-flex items-center gap-1.5">
            {domain.liveness_status && <LivenessDot status={domain.liveness_status} />}
            <Link to={`/domains/${domain.id}`} className="text-brand-600 hover:text-brand-800 font-medium">
              {domain.domain_name}
            </Link>
            {canSeeWhois && domain.is_exposed && (
              <ShieldAlert
                className="h-4 w-4 text-red-500 shrink-0"
                aria-label="WHOIS exposed"
              >
                <title>Personal info exposed in WHOIS</title>
              </ShieldAlert>
            )}
          </span>
        );
      case 'tld':
        return <span className="text-xs font-mono text-slate-500">.{domain.tld || '?'}</span>;
      case 'registrar':
        return <span className="text-slate-500"><RegistrarLabel compact fallback={domain.registrar_name || domain.registrar_id} current={domain.current_registrar} /></span>;
      case 'expiry':
        return domain.expiry_date ? format(new Date(domain.expiry_date), 'MMM d, yyyy') : '-';
      case 'status':
        return <DomainStatusBadge size="md" status={domain.status} />;
      case 'liveness':
        return domain.liveness_status
          ? <LivenessBadge status={domain.liveness_status} />
          : <span className="text-slate-300">-</span>;
      case 'autoRenew':
        return domain.auto_renew === null
          ? <span className="text-slate-300">-</span>
          : domain.auto_renew
            ? <span className="text-emerald-600">Yes</span>
            : <span className="text-slate-400">No</span>;
      case 'tags':
        return (
          <div className="flex flex-wrap gap-1">
            {domain.tags?.slice(0, 3).map(tag => (
              <span key={tag.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                style={{ backgroundColor: tag.color + '20', color: tag.color }}>
                {tag.name}
              </span>
            ))}
            {domain.tags && domain.tags.length > 3 && (
              <span className="text-xs text-slate-500">+{domain.tags.length - 3}</span>
            )}
          </div>
        );
      case 'geo':
        return domain.geo?.length
          ? <span className="inline-flex items-center gap-1">
              <GeoList value={domain.geo} max={3} />
              {domain.geo_mismatch && <span title={`Cove targets ${domain.cove_geo?.join(', ')} — different from DomainHQ`} className="text-amber-500">⚠</span>}
            </span>
          : <span className="text-slate-400">-</span>;
      case 'domainType':
        return domain.domain_type
          ? <span className="text-slate-600">{domain.domain_type}</span>
          : <span className="text-slate-400">-</span>;
      case 'tier':
        return domain.tier
          ? <TierBadge tier={domain.tier} />
          : <span className="text-slate-400">-</span>;
      case 'vertical':
        return domain.vertical?.length
          ? (
            <span className="inline-flex flex-wrap gap-1">
              {domain.vertical.map((v) => <span key={v} className="rounded bg-violet-50 px-1.5 py-0.5 text-xs font-medium text-violet-700">{v}</span>)}
            </span>
          )
          : <span className="text-slate-400">-</span>;
      case 'niche':
      case 'subniche':
      case 'cloakingType':
      case 'subreddit': {
        const fieldMap: Record<string, string> = {
          niche: 'niche', subniche: 'subniche',
          cloakingType: 'cloaking_type', subreddit: 'subreddit',
        };
        const val = (domain as unknown as Record<string, unknown>)[fieldMap[col.key]] as string | null;
        return val ? <span className="text-slate-600">{val}</span> : <span className="text-slate-400">-</span>;
      }
      case 'source': {
        const sourceLabels: Record<string, string> = {
          'manual': 'Manual',
          'csv_import': 'CSV Import',
          'registrar-api': 'Registrar API',
        };
        return <span className="text-slate-500">{sourceLabels[domain.source] || domain.source}</span>;
      }
      case 'nameservers': {
        const ns = parseNameserverList(domain.nameservers);
        const pending = openNsChanges.get(domain.id);
        const shown = pending ? pending.requested_nameservers : ns;
        if (shown.length === 0) return <span className="text-slate-300">-</span>;
        return (
          <div className="space-y-0.5">
            {shown.map((h) => <div key={h} className={`text-xs font-mono whitespace-nowrap ${pending ? 'text-slate-400' : 'text-slate-600'}`}>{h}</div>)}
            {pending && (
              <Link
                to={`/domains/${domain.id}`}
                title={pending.message || (pending.registrar_status ? `${registrarName(pending.registrar_type)}: ${pending.registrar_status}` : `Waiting for ${registrarName(pending.registrar_type)} to confirm`)}
                className={`inline-flex items-center gap-1 text-[11px] font-medium ${pending.status === 'stalled' ? 'text-orange-700' : 'text-amber-700'}`}
              >
                <Clock className="h-3 w-3" />{pending.status === 'stalled' ? 'Stalled' : 'Pending'}
              </Link>
            )}
          </div>
        );
      }
      case 'acquisitionType':
        return domain.acquisition_method
          ? <span className="text-slate-600">{domain.acquisition_method}</span>
          : <span className="text-slate-400">-</span>;
      case 'cost': {
        if (domain.cost == null) return <span className="text-slate-400">-</span>;
        const shown = costsRevealed || revealedCostIds.has(domain.id);
        return (
          <button
            type="button"
            onClick={() => toggleCostRow(domain.id)}
            title={shown ? 'Hide cost' : 'Click to reveal cost'}
            aria-label={shown ? 'Hide cost' : 'Reveal cost'}
            aria-pressed={shown}
            className={`text-slate-700 whitespace-nowrap rounded transition-[filter] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
              shown ? '' : 'blur-[5px] select-none cursor-pointer'
            }`}
          >
            {formatCost(domain.cost, domain.cost_currency)}
          </button>
        );
      }
      case 'acquired':
        return domain.acquisition_date
          ? <span className="text-slate-600 whitespace-nowrap">{format(new Date(`${domain.acquisition_date}T00:00:00`), 'MMM d, yyyy')}</span>
          : <span className="text-slate-400">-</span>;
      default:
        return '-';
    }
  }

  return (
    <div>
      <div className="mb-4">
        <PageHeader
          title="Domains"
          description={loading ? undefined : `${total.toLocaleString()} domain${total === 1 ? '' : 's'}${anyFilterActive ? ' match the current filters' : ''}`}
          actions={<>
            {isAdmin && (
              <button onClick={handleExport} disabled={exporting || loading || total === 0} className="btn btn-secondary"
                title={anyFilterActive ? 'Download the domains matching the current filters as CSV' : 'Download all domains as CSV (set filters to narrow it)'}>
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting…' : `Export ${total.toLocaleString()}`}
              </button>
            )}
            <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Add Domain
            </button>
          </>}
        />
      </div>

      {/* Quick views */}
      <div className="mb-3">
        <DomainViews
          params={searchParams}
          onApply={(q) => {
            const next = new URLSearchParams(q);
            for (const k of ['sort_by', 'sort_order']) { const v = searchParams.get(k); if (v) next.set(k, v); }
            setSearchParams(next);
            setColFilterDrafts({});
          }}
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {/* Quick search */}
        <div className="relative flex-1 min-w-[12rem] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search domains..."
            value={colFilterDrafts['search'] ?? search}
            // URLs ("https://www.example.com/page") search as the bare domain: a
            // paste is rewritten straight away, typed text when the box loses focus.
            onChange={e => setColFilter('search', e.target.value)}
            onPaste={e => {
              const pasted = e.clipboardData.getData('text');
              const domain = toSearchText(pasted);
              if (domain !== pasted) { e.preventDefault(); setColFilter('search', domain); }
            }}
            onBlur={e => { const d = toSearchText(e.target.value); if (d !== e.target.value) setColFilterImmediate('search', d); }}
            className="input pl-9 py-1.5 text-sm"
          />
        </div>

        {/* Toggle column filters */}
        <button
          onClick={() => {
            const next = !showColFilters;
            setShowColFilters(next);
            localStorage.setItem(SHOW_COL_FILTERS_KEY, String(next));
          }}
          className={`btn btn-secondary py-1.5 text-sm ${showColFilters ? 'border-brand-300 bg-brand-50/50 text-brand-700' : ''}`}
          title="Toggle column filters"
        >
          <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
          Filters
          {anyFilterActive && (
            <span className="ml-1.5 bg-brand-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              !
            </span>
          )}
        </button>

        {/* Expiring dropdown (no column equivalent) */}
        <select
          value={expiringDaysFilter}
          onChange={e => updateFilter('expiring_days', e.target.value)}
          className={`input w-auto py-1.5 text-sm ${expiringDaysFilter ? 'border-brand-300 bg-brand-50/50' : ''}`}
        >
          <option value="">Expiring</option>
          <option value="7">7 days</option>
          <option value="30">30 days</option>
          <option value="60">60 days</option>
          <option value="90">90 days</option>
        </select>

        {(expiresFromFilter || expiresToFilter) && (
          <span className="inline-flex items-center gap-1 rounded-full border border-brand-300 bg-brand-50/50 px-2.5 py-1 text-xs text-slate-700">
            Expires {expiresFromFilter || '…'} → {expiresToFilter ? new Date(Date.parse(`${expiresToFilter}T00:00:00Z`) - 86400000).toISOString().slice(0, 10) : '…'}
            <button type="button" aria-label="Clear expiry range" className="ml-0.5 text-slate-400 hover:text-slate-700"
              onClick={() => { const p = new URLSearchParams(searchParams); p.delete('expires_from'); p.delete('expires_to'); p.set('page', '1'); setSearchParams(p); }}>×</button>
          </span>
        )}

        {/* Row density */}
        <button
          onClick={toggleDensity}
          className="btn btn-secondary py-1.5 text-sm"
          title={compact ? 'Comfortable rows' : 'Compact rows'}
          aria-pressed={compact}
        >
          {compact ? <Rows2 className="h-3.5 w-3.5" /> : <Rows3 className="h-3.5 w-3.5" />}
        </button>

        {/* Columns picker */}
        <div className="relative" ref={columnPickerRef}>
          <button
            onClick={() => setShowColumnPicker(!showColumnPicker)}
            className="btn btn-secondary py-1.5 text-sm"
            title="Toggle columns"
          >
            <Columns className="h-3.5 w-3.5" />
          </button>
          {showColumnPicker && (
            <div className="absolute right-0 mt-2 w-52 bg-surface rounded-xl shadow-lg border border-slate-200 z-20 py-2">
              <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Columns</div>
              {availableColumns.map(col => (
                <label key={col.key} className="flex items-center gap-2 px-3 py-1 hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)}
                    className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 h-3.5 w-3.5" />
                  <span className="text-sm text-slate-700">{col.label}</span>
                </label>
              ))}
              <div className="border-t border-slate-100 mt-1 pt-1 px-3 flex justify-between">
                <button onClick={() => {
                  const defaults = new Set(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
                  setVisibleColumns(defaults);
                  localStorage.setItem(KNOWN_COLUMNS_KEY, JSON.stringify(ALL_COLUMNS.map(c => c.key)));
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify([...defaults]));
                }} className="text-xs text-brand-600 hover:text-brand-800 py-1">Reset visibility</button>
                <button onClick={() => {
                  const def = ALL_COLUMNS.map(c => c.key);
                  setColumnOrder(def);
                  localStorage.setItem(ORDER_KEY, JSON.stringify(def));
                  localStorage.setItem(ORDER_VERSION_KEY, ORDER_VERSION.toString());
                }} className="text-xs text-brand-600 hover:text-brand-800 py-1">Reset order</button>
              </div>
            </div>
          )}
        </div>

        {/* Clear all */}
        {anyFilterActive && (
          <button
            onClick={() => {
              setSearchParams({});
              setColFilterDrafts({});
            }}
            className="btn btn-secondary py-1.5 text-sm text-slate-500"
            title="Clear all filters"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Bulk action bar — floats over the bottom of the content area while rows are selected */}
      {canSelect && selectedIds.size > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-5 z-40 flex justify-center px-4 lg:pl-[calc(var(--sidebar-w,16rem)+1rem)]">
        <div className="pointer-events-auto flex max-w-full flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-surface px-4 py-2.5 shadow-2xl ring-1 ring-slate-900/5 dark:ring-white/10 animate-[popIn_140ms_ease-out]">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-900">
            <span className="flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-brand-600 px-1.5 text-xs font-semibold text-white">{selectedIds.size}</span>
            selected
          </span>
          <span className="h-5 w-px bg-slate-200" />

          {/* Renew (super admin or renew grant; GoDaddy/Porkbun/Namecheap API-synced domains; max 50) */}
          {canRenewDomains && (
            <button
              onClick={() => setRenewOpen(true)}
              disabled={bulkBusy || selectedIds.size > 50}
              title={selectedIds.size > 50 ? 'Select at most 50 domains to renew at once' : 'Renew the selected domains (charges the registrar accounts)'}
              className="btn btn-secondary py-1 text-sm disabled:opacity-50"
            >
              <CalendarClock className="h-3.5 w-3.5 mr-1.5" />
              Renew…
            </button>
          )}

          {/* Set nameservers (admin or NS grant; applies only to Ilkari/Porkbun/Safenames/GoDaddy API-synced domains) */}
          {canManageNameservers && (
            <button onClick={() => setNsBulkOpen(true)} disabled={bulkBusy} className="btn btn-secondary py-1 text-sm disabled:opacity-50">
              <Globe className="h-3.5 w-3.5 mr-1.5" />
              Set nameservers
            </button>
          )}

          {canClassify && (<>
          {/* Set type */}
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500">Set type:</label>
            <select
              value=""
              disabled={bulkBusy}
              onChange={e => {
                const v = e.target.value;
                if (!v) return;
                applyBulkType(v === '__clear__' ? null : v);
                e.target.value = '';
              }}
              className="text-sm border border-slate-300 rounded px-2 py-1 bg-surface focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none disabled:opacity-50"
            >
              <option value="">Choose…</option>
              {(metaOptions.domain_type || []).map(t => <option key={t} value={t}>{t}</option>)}
              <option value="__clear__">— Clear type —</option>
            </select>
          </div>

          {/* Edit tags */}
          <div className="relative" ref={tagPopoverRef}>
            <button
              onClick={() => setTagPopoverOpen(o => !o)}
              disabled={bulkBusy}
              className="btn btn-secondary py-1 text-sm disabled:opacity-50"
            >
              <Tags className="h-3.5 w-3.5 mr-1.5" />
              Edit tags
            </button>
            {tagPopoverOpen && (
              <div className="absolute left-0 bottom-full mb-2 w-64 bg-surface rounded-xl shadow-lg border border-slate-200 z-30 p-3">
                {/* Mode */}
                <div className="flex gap-1 mb-2 bg-slate-100 rounded-lg p-0.5">
                  {(['add', 'remove', 'set'] as const).map(m => (
                    <button
                      key={m}
                      onClick={() => setTagMode(m)}
                      className={`flex-1 text-xs py-1 rounded-md capitalize transition-colors ${tagMode === m ? 'bg-surface shadow-sm text-brand-700 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      {m === 'set' ? 'Replace' : m}
                    </button>
                  ))}
                </div>
                <div className="max-h-56 overflow-y-auto -mx-1 px-1">
                  {tags.length === 0 ? (
                    <div className="text-xs text-slate-400 py-2 text-center">No tags yet</div>
                  ) : tags.map(t => (
                    <label key={t.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tagDraft.has(t.id)}
                        onChange={() => toggleTagDraft(t.id)}
                        className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 h-3.5 w-3.5"
                      />
                      <span className="text-sm text-slate-700">{t.name}</span>
                    </label>
                  ))}
                </div>
                {tagMode === 'set' && tagDraft.size === 0 && (
                  <div className="text-[11px] text-amber-600 mt-2">Replace with no tags will clear all tags.</div>
                )}
                <button
                  onClick={applyBulkTags}
                  disabled={bulkBusy}
                  className="btn btn-primary w-full py-1 text-sm mt-2 disabled:opacity-50"
                >
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                  Apply to {selectedIds.size}
                </button>
              </div>
            )}
          </div>
          </>)}

          {bulkBusy && <RefreshCw className="h-4 w-4 text-brand-600 animate-spin" />}

          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            title="Clear selection (Esc)"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>
        </div>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {loading && domains.length === 0 ? (
          <table className="w-full"><tbody><SkeletonRows rows={12} cols={Math.min(activeColumns.length + 1, 8)} /></tbody></table>
        ) : domains.length === 0 ? (
          <EmptyState
            icon={SearchX}
            title={anyFilterActive ? 'No domains match these filters' : 'No domains yet'}
            description={anyFilterActive ? 'Try a different view or clear the filters.' : 'Sync a registrar account or add a domain to get started.'}
            action={anyFilterActive
              ? <button className="btn btn-secondary" onClick={() => { setSearchParams({}); setColFilterDrafts({}); }}>Clear filters</button>
              : <button className="btn btn-primary" onClick={() => setShowAddModal(true)}><Plus className="h-4 w-4 mr-2" />Add Domain</button>}
          />
        ) : (
          <>
            {/* Phones: one card per domain instead of the wide table. */}
            <ul className={`divide-y divide-slate-100 md:hidden ${loading ? 'opacity-60' : ''}`}>
              {domains.map((domain) => {
                const d = domain as Domain & { registrar_name?: string };
                const selected = selectedIds.has(d.id);
                return (
                  <li key={d.id} className={`flex gap-3 px-4 py-3 ${selected ? 'bg-brand-50/60' : ''}`}>
                    {canSelect && (
                      <input type="checkbox" checked={selected} onChange={() => toggleRow(d.id)}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500" aria-label={`Select ${d.domain_name}`} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <LivenessDot status={d.liveness_status} />
                        <Link to={`/domains/${d.id}`} className="truncate font-medium text-brand-600">{d.domain_name}</Link>
                        <span className="ml-auto shrink-0"><DomainStatusBadge size="md" status={d.status} /></span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        {d.domain_type && <span className="font-medium text-slate-700">{d.domain_type}</span>}
                        {d.geo?.length > 0 && <GeoList value={d.geo} max={3} />}
                        <span className="truncate"><RegistrarLabel compact fallback={d.registrar_name || d.registrar_id} current={d.current_registrar} /></span>
                        {d.expiry_date && (
                          <span>
                            Expires {format(new Date(d.expiry_date), 'MMM d, yyyy')}
                            {d.auto_renew === 0 && <span className="ml-1 text-amber-600">· no auto-renew</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Own scroll box so the header row and the first column stay put
                while scrolling in either direction. */}
            <div className={`relative hidden md:block overflow-auto max-h-[calc(100vh-17rem)] min-h-[16rem] ${compact ? 'density-compact' : ''} ${loading ? 'opacity-60 transition-opacity' : ''}`}>
              <table className="w-full border-separate border-spacing-0">
                <thead>
                  {/* Header row — draggable + sortable */}
                  <tr>
                    {canSelect && (
                      <th className="table-header w-10 px-3 sticky-col left-0 z-20">
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          checked={allOnPageSelected}
                          onChange={toggleAllOnPage}
                          className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 h-3.5 w-3.5"
                          title="Select all on this page"
                        />
                      </th>
                    )}
                    {activeColumns.map((col, ci) => {
                      const isSorted = col.sortKey && sortBy === col.sortKey;
                      return (
                        <th
                          key={col.key}
                          style={ci === 0 ? { left: canSelect ? 40 : 0 } : undefined}
                          draggable
                          onDragStart={() => handleDragStart(col.key)}
                          onDragOver={e => handleDragOver(e, col.key)}
                          onDrop={handleDrop}
                          className={`table-header group select-none ${ci === 0 ? 'sticky-col sticky-col-edge z-20' : ''} ${classificationColumns.has(col.key) ? 'bg-violet-50' : ''}`}
                        >
                          <div className="flex items-center gap-1">
                            <GripVertical className="h-3 w-3 text-slate-300 opacity-0 group-hover:opacity-100 cursor-grab shrink-0 transition-opacity" />
                            <span className={`${col.sortKey ? 'cursor-pointer hover:text-slate-700' : ''}`}
                              onClick={() => col.sortKey && toggleSort(col.sortKey)}>
                              {col.label}
                            </span>
                            {col.sortKey && (
                              <span className="shrink-0">
                                {isSorted && sortOrder === 'asc' && <ArrowUp className="h-3 w-3 text-brand-500" />}
                                {isSorted && sortOrder === 'desc' && <ArrowDown className="h-3 w-3 text-brand-500" />}
                                {!isSorted && <ArrowUpDown className="h-3 w-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />}
                              </span>
                            )}
                            {col.key === 'cost' && (
                              // stopPropagation so revealing doesn't also sort
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleCostColumn(); }}
                                title={costsRevealed ? 'Hide all costs' : 'Reveal all costs'}
                                aria-label={costsRevealed ? 'Hide all costs' : 'Reveal all costs'}
                                aria-pressed={costsRevealed}
                                className="shrink-0 text-slate-400 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                              >
                                {costsRevealed
                                  ? <EyeOff className="h-3.5 w-3.5" />
                                  : <Eye className="h-3.5 w-3.5" />}
                              </button>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                  {/* Filter row */}
                  {showColFilters && (
                    <tr className="bg-slate-50/80">
                      {canSelect && <th className="w-10 px-3 border-b border-slate-200 sticky-col left-0 bg-slate-50" />}
                      {activeColumns.map((col, ci) => (
                        <th key={col.key} style={ci === 0 ? { left: canSelect ? 40 : 0 } : undefined}
                          className={`px-3 py-1.5 border-b border-slate-200 ${ci === 0 ? 'sticky-col sticky-col-edge bg-slate-50' : ''} ${classificationColumns.has(col.key) ? 'bg-violet-50/60' : ''}`}>
                          {renderFilterInput(col)}
                        </th>
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {domains.map((domain, idx) => (
                    <tr key={domain.id} className={`table-row group/row ${idx % 2 === 1 ? 'table-row-alt' : ''} ${selectedIds.has(domain.id) ? 'is-selected' : ''}`}>
                      {canSelect && (
                        <td className="table-cell w-10 px-3 sticky-col left-0">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(domain.id)}
                            onChange={() => toggleRow(domain.id)}
                            className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 h-3.5 w-3.5"
                          />
                        </td>
                      )}
                      {activeColumns.map((col, ci) => (
                        <td key={col.key} style={ci === 0 ? { left: canSelect ? 40 : 0 } : undefined}
                          className={`table-cell ${ci === 0 ? 'sticky-col sticky-col-edge' : ''} ${classificationColumns.has(col.key) ? 'bg-violet-50/40' : ''}`}>
                          {renderCellValue(col, domain as Domain & { registrar_name?: string; tags?: Tag[] })}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={`flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 ${canSelect && selectedIds.size > 0 ? 'mb-20' : ''}`}>
              <div className="text-xs sm:text-sm text-slate-500">
                <span className="hidden sm:inline">Showing </span>{(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}<span className="hidden sm:inline"> domains</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const p = new URLSearchParams(searchParams);
                    p.set('page', String(page - 1));
                    setSearchParams(p);
                  }}
                  disabled={page === 1}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="flex items-center px-3 text-sm text-slate-700">Page {page} of {totalPages}</span>
                <button
                  onClick={() => {
                    const p = new URLSearchParams(searchParams);
                    p.set('page', String(page + 1));
                    setSearchParams(p);
                  }}
                  disabled={page >= totalPages}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showAddModal && (
        <AddDomainModal
          accounts={accounts}
          tags={tags}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); loadDomains(); }}
        />
      )}

      {renewOpen && (
        <RenewModal
          domainIds={Array.from(selectedIds)}
          onClose={() => setRenewOpen(false)}
          onDone={() => loadDomains()}
        />
      )}

      {nsBulkOpen && (
        <NameserverEditModal
          title={`Set nameservers — ${selectedIds.size} domain(s)`}
          notice={`Applies to the selected Ilkari/Porkbun/Safenames/GoDaddy API-synced domains (${selectedIds.size} selected; others are skipped).`}
          busy={bulkBusy}
          onClose={() => setNsBulkOpen(false)}
          onSubmit={applyBulkNameservers}
        />
      )}
    </div>
  );
}

